import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useCurrency, CURRENCIES } from "@/lib/currency";
import { useSettings } from "@/lib/settings";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { User, LogOut, LayoutDashboard, CalendarDays, ChevronDown, Globe, Coins } from "lucide-react";

export default function SiteHeader({ transparent = false }) {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const { currency, setCurrency } = useCurrency();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const enabledLangs = (settings?.enabled_languages && settings.enabled_languages.length > 0)
    ? settings.enabled_languages
    : ["fr", "en"];
  const enabledCurrencies = (settings?.enabled_currencies && settings.enabled_currencies.length > 0)
    ? settings.enabled_currencies.filter((c) => CURRENCIES.includes(c))
    : CURRENCIES;

  const nav = [
    { to: "/", label: t("nav_home") },
    { to: "/search", label: t("nav_vehicles") },
    { to: "/how-it-works", label: t("nav_how") },
    { to: "/about", label: t("nav_about") },
    { to: "/contact", label: t("nav_contact") },
  ];

  const pillClass = transparent
    ? "text-white bg-white/10 hover:bg-white/15 border-white/10"
    : "text-[#0a1e3f] bg-gray-100 hover:bg-gray-200 border-transparent";

  return (
    <header className={`sticky top-0 z-40 border-b ${transparent ? "border-white/10 bg-transparent" : "border-gray-100 bg-white/95 backdrop-blur-xl"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
        <Logo variant={transparent ? "light" : "dark"} />
        <nav className="hidden lg:flex items-center gap-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  transparent
                    ? isActive ? "text-white bg-white/10" : "text-white/90 hover:bg-white/10"
                    : isActive ? "text-[#0a1e3f] bg-[#0a1e3f]/5" : "text-gray-700 hover:text-[#0a1e3f]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {/* Language selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="lang-selector" className={`hidden md:flex items-center text-xs font-semibold gap-1 px-3 py-1.5 rounded-full border ${pillClass}`}>
                <Globe className="h-3.5 w-3.5" /> {lang.toUpperCase()} <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuLabel>Langue / Language</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {enabledLangs.includes("fr") && <DropdownMenuItem data-testid="lang-fr" onClick={() => setLang("fr")}>🇫🇷 Français {lang === "fr" && "✓"}</DropdownMenuItem>}
              {enabledLangs.includes("en") && <DropdownMenuItem data-testid="lang-en" onClick={() => setLang("en")}>🇬🇧 English {lang === "en" && "✓"}</DropdownMenuItem>}
              {enabledLangs.includes("es") && <DropdownMenuItem data-testid="lang-es" onClick={() => setLang("es")}>🇪🇸 Español {lang === "es" && "✓"}</DropdownMenuItem>}
              {enabledLangs.includes("pt") && <DropdownMenuItem data-testid="lang-pt" onClick={() => setLang("pt")}>🇵🇹 Português {lang === "pt" && "✓"}</DropdownMenuItem>}
              {enabledLangs.includes("ar") && <DropdownMenuItem data-testid="lang-ar" onClick={() => setLang("ar")}>🇸🇦 العربية {lang === "ar" && "✓"}</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Currency selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="currency-selector" className={`hidden md:flex items-center text-xs font-semibold gap-1 px-3 py-1.5 rounded-full border ${pillClass}`}>
                <Coins className="h-3.5 w-3.5" /> {currency} <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 max-h-72 overflow-auto">
              <DropdownMenuLabel>{lang === "fr" ? "Devise" : "Currency"}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {enabledCurrencies.map((c) => (
                <DropdownMenuItem key={c} data-testid={`currency-${c}`} onClick={() => setCurrency(c)}>
                  {c} {currency === c && "✓"}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-btn" className="mbk-btn-primary text-white gap-2 h-10 px-4">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">{user.full_name?.split(" ")[0] || t("nav_account")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <div className="text-sm font-semibold text-[#0a1e3f]">{user.full_name}</div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <LayoutDashboard className="h-4 w-4 mr-2" /> {t("nav_dashboard")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-my-bookings" onClick={() => navigate("/my-bookings")}>
                  <CalendarDays className="h-4 w-4 mr-2" /> {t("nav_my_bookings")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" /> {t("nav_logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login">
              <Button data-testid="header-login-btn" className="mbk-btn-primary text-white gap-2 h-10 px-5">
                <User className="h-4 w-4" /> {t("nav_account")}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
